/**
 * 箱子码：**浏览器 `window.print()`**，单张 **98mm × 88mm**（与内销产品标签 `browserLabelPrint.ts` 一致）。
 * 版式：上方 QR（载荷为箱子码字符串）、下方居中明文；**不做整页 rotate**。
 * 二维码栅格宽度按 **203dpi** 与 `.box-qr` 物理宽度估算，减轻热敏机上的缩放发糊。
 */
import QRCode from 'qrcode'

/** 与内销标签、`@page` 一致：宽 98mm、高 88mm */
const LABEL_PAGE_W_MM = 98
const LABEL_PAGE_H_MM = 88

/** 打印页字体栈（与 `browserLabelPrint` 一致） */
const PRINT_FONT_FAMILY = `'Microsoft YaHei', 'Microsoft YaHei UI', 'Segoe UI', Arial, sans-serif`

/** 二维码物理宽度（mm），与 `.box-qr` 一致；略大于原「缩小 1/3」以免码过小 */
const BOX_QR_DISPLAY_MM = 50

/** 热敏机常用 203dpi；用于按物理宽度估算 QR PNG 像素 */
const LABEL_PRINTER_DPI = 203

/** 超采样系数：略高于物理点数，减轻模块与点阵不对齐时的边缘发糊 */
const BOX_QR_RASTER_OVERSAMPLE = 2.4

function boxCodeQrToDataUrlWidthPx (): number {
  const dots = (BOX_QR_DISPLAY_MM / 25.4) * LABEL_PRINTER_DPI
  const w = Math.round(dots * BOX_QR_RASTER_OVERSAMPLE)
  return Math.min(1024, Math.max(400, w))
}

function escapeHtml (s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function boxCodePrintStyles (): string {
  return `
@page {
  size: ${LABEL_PAGE_W_MM}mm ${LABEL_PAGE_H_MM}mm;
  margin: 0;
}
@page :first {
  size: ${LABEL_PAGE_W_MM}mm ${LABEL_PAGE_H_MM}mm;
  margin: 0;
}
html, body { margin: 0 !important; padding: 0 !important; }
body {
  font-family: ${PRINT_FONT_FAMILY};
  color: #000;
  background: #fff;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.label-page {
  box-sizing: border-box;
  width: ${LABEL_PAGE_W_MM}mm;
  height: ${LABEL_PAGE_H_MM}mm;
  max-height: ${LABEL_PAGE_H_MM}mm;
  padding: 4mm 5mm;
  overflow: hidden;
  page-break-after: always;
  break-after: page;
  break-inside: avoid;
  page-break-inside: avoid;
}
.label-page:last-of-type {
  page-break-after: auto;
  break-after: auto;
}

.box-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.box-qr {
  display: block;
  width: ${BOX_QR_DISPLAY_MM}mm;
  max-width: 86%;
  height: auto;
  object-fit: contain;
  /* 高分辨率 PNG 缩放到 mm 时，避免双线性插值把模块边缘抹糊 */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.box-text {
  margin-top: 3mm;
  /* 与当前码宽比例略收小，避免字压过码区 */
  font-size: max(18px, 4.5mm);
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  word-break: break-all;
  max-width: 100%;
  padding: 0 1mm;
  text-rendering: geometricPrecision;
  font-variant-numeric: tabular-nums;
}
`
}

function buildSingleSection (plain: string, dataUrl: string): string {
  return `<section class="label-page"><div class="box-inner"><img class="box-qr" src="${dataUrl}" alt="" /><div class="box-text">${escapeHtml(plain)}</div></div></section>`
}

function buildBoxCodePrintDocument (items: { plain: string; dataUrl: string }[]): string {
  const sections = items.map(({ plain, dataUrl }) => buildSingleSection(plain, dataUrl)).join('')
  return `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="UTF-8"/><title>箱子码</title><style>${boxCodePrintStyles()}</style></head><body>${sections}</body></html>`
}

function printHtmlInHiddenIframe (html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('title', '')
    Object.assign(iframe.style, {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      width: `${LABEL_PAGE_W_MM}mm`,
      height: '2400px',
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '-1'
    })
    document.body.appendChild(iframe)
    const win = iframe.contentWindow
    const doc = iframe.contentDocument
    if (!win || !doc) {
      iframe.remove()
      reject(new Error('无法创建打印框架'))
      return
    }

    const cleanup = (): void => {
      try {
        iframe.remove()
      } catch {
        /* */
      }
      resolve()
    }

    const runPrint = (): void => {
      const d = win.document
      const sh = Math.max(
        d.documentElement?.scrollHeight ?? 0,
        d.body?.scrollHeight ?? 0,
        Math.ceil((LABEL_PAGE_H_MM / 25.4) * 96)
      )
      iframe.style.height = `${sh + 24}px`
      win.focus()
      try {
        win.print()
      } catch (err: unknown) {
        iframe.remove()
        reject(err instanceof Error ? err : new Error(String(err)))
        return
      }
      win.addEventListener('afterprint', cleanup, { once: true })
      window.setTimeout(() => {
        if (iframe.isConnected) cleanup()
      }, 90_000)
    }

    doc.open()
    doc.write(html)
    doc.close()

    const schedulePrint = (): void => {
      requestAnimationFrame(() => {
        requestAnimationFrame(runPrint)
      })
    }

    if (doc.readyState === 'complete') {
      schedulePrint()
    } else {
      iframe.addEventListener('load', schedulePrint, { once: true })
    }
  })
}

/**
 * 在浏览器中按 98×88mm 逐张打印箱子码（QR + 下方明文）。
 */
export async function printBoxCodesBrowser (codes: string[]): Promise<void> {
  const list = codes.map(c => String(c ?? '').trim()).filter(Boolean)
  if (list.length === 0) return

  const items = await Promise.all(
    list.map(async (plain) => {
      const dataUrl = await QRCode.toDataURL(plain, {
        errorCorrectionLevel: 'M',
        margin: 3,
        width: boxCodeQrToDataUrlWidthPx(),
        type: 'image/png',
        color: { dark: '#000000', light: '#ffffff' }
      })
      return { plain, dataUrl }
    })
  )

  const html = buildBoxCodePrintDocument(items)
  await printHtmlInHiddenIframe(html)
}
